import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import useStyles from './CategoryLinkGallery.styles';
import Icon from '../Icon/Icon';
import clsx from 'clsx';
import { useTheme } from '@mui/material';
import CategoryLinksTextAndIcon from './variants/CategoryLinkGalleryTextAndIcon';
import CategoryLinksSmallImage from './variants/CategoryLinkGallerySmallImage';
import CategoryLinksLargeImage from './variants/CategoryLinkGalleryLargeImage';
import { useLoaded } from '../MainContent/useComponentLoading';
const SMALL_IMAGE_ITEMS_DESKTOP = 10.5;
const SMALL_IMAGE_ITEMS_SMALL = 7;
const SMALL_IMAGE_ITEMS_TABLET = 5;
const SMALL_IMAGE_ITEMS_MOBILE = 2;
const LARGE_IMAGE_ITEMS_DESKTOP = 7.5;
const LARGE_IMAGE_ITEMS_SMALL = 5;
const LARGE_IMAGE_ITEMS_TABLET = 3;
const LARGE_IMAGE_ITEMS_MOBILE = 2;
const ITEM_GAP = 4;
export const SMALL_IMAGE_ITEM_WIDTH = 148;
export const LARGE_IMAGE_ITEM_WIDTH = 240;
export const IMAGE_ITEM_WIDTH_MOBILE = 140;
const TEXT_ICON = 'TextAndIcon';
const SMALL_IMAGE = 'SmallImage';
const LARGE_IMAGE = 'LargeImage';
const getCarouselMetrics = (itemCount, visibleItems, itemWidth, viewportWidth) => {
    const safeItemCount = Math.max(0, itemCount);
    const trackWidth = safeItemCount * itemWidth + Math.max(0, safeItemCount - 1) * ITEM_GAP;
    const visibleSlots = Math.ceil(visibleItems);
    const fallbackViewportWidth = visibleItems * itemWidth + Math.max(0, visibleSlots - 1) * ITEM_GAP;
    const step = itemWidth + ITEM_GAP;
    const resolvedViewportWidth = viewportWidth !== null && viewportWidth !== void 0 ? viewportWidth : fallbackViewportWidth;
    const maxOffset = Math.max(0, trackWidth - resolvedViewportWidth);
    return { viewportWidth: resolvedViewportWidth, step, maxOffset };
};
const resolveGalleryImage = (item) => {
    var _a;
    if (item.image) {
        return item.image;
    }
    if (typeof item.linkType === 'string' || !Array.isArray(item.linkType)) {
        return undefined;
    }
    return (_a = item.linkType.find((link) => link.type === 'Image')) === null || _a === void 0 ? void 0 : _a.image;
};
const CategoryLinkGallery = (props) => {
    const { linkGalleryItems, title, className, galleryType = TEXT_ICON, showArrows = true, sendCategoryCarouselClickAnalytics, } = props;
    const { classes, cx } = useStyles();
    const theme = useTheme();
    const { sm, md, xl } = theme.breakpoints.values;
    const categories = linkGalleryItems !== null && linkGalleryItems !== void 0 ? linkGalleryItems : [];
    const scrollContainerRef = useRef(null);
    const smallImageCarouselViewportRef = useRef(null);
    const largeImageCarouselViewportRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [isDesktop, setIsDesktop] = useState(true);
    const [smallImageItemsPerView, setSmallImageItemsPerView] = useState(1);
    const [largeImageItemsPerView, setLargeImageItemsPerView] = useState(1);
    const [smallImageViewportWidth, setSmallImageViewportWidth] = useState(0);
    const [largeImageViewportWidth, setLargeImageViewportWidth] = useState(0);
    const [smallImageOffset, setSmallImageOffset] = useState(0);
    const [largeImageOffset, setLargeImageOffset] = useState(0);
    const [isTextIconOverflowing, setIsTextIconOverflowing] = useState(false);
    const normalizedGalleryType = galleryType === 'CategoryLinkTextAndIcon' ? TEXT_ICON : galleryType;
    useLoaded(props);
    useEffect(() => {
        if (!globalThis.window)
            return;
        const handleResize = () => {
            setIsDesktop(globalThis.window.innerWidth >= 960);
        };
        handleResize();
        globalThis.window.addEventListener('resize', handleResize);
        return () => globalThis.window.removeEventListener('resize', handleResize);
    }, []);
    const checkScroll = useCallback(() => {
        if (!scrollContainerRef.current)
            return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        const isOverflowing = scrollWidth > clientWidth + 10;
        if (normalizedGalleryType === TEXT_ICON) {
            setIsTextIconOverflowing(isOverflowing);
        }
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }, [normalizedGalleryType]);
    useEffect(() => {
        if (normalizedGalleryType !== TEXT_ICON) {
            setIsTextIconOverflowing(false);
        }
    }, [normalizedGalleryType]);
    useEffect(() => {
        if (!globalThis.window)
            return;
        checkScroll();
        globalThis.window.addEventListener('resize', checkScroll);
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
        }
        return () => {
            globalThis.window.removeEventListener('resize', checkScroll);
            if (container) {
                container.removeEventListener('scroll', checkScroll);
            }
        };
    }, [checkScroll]);
    const getItemsPerView = useCallback((viewportWidth, desktop, small, tablet, mobile) => {
        if (viewportWidth >= xl)
            return desktop;
        if (viewportWidth >= md)
            return small;
        if (viewportWidth >= sm)
            return tablet;
        return mobile;
    }, [md, sm, xl]);
    useEffect(() => {
        if (normalizedGalleryType !== SMALL_IMAGE) {
            return;
        }
        if (!globalThis.window)
            return;
        const updateItemsPerView = () => {
            setSmallImageItemsPerView(getItemsPerView(globalThis.window.innerWidth, SMALL_IMAGE_ITEMS_DESKTOP, SMALL_IMAGE_ITEMS_SMALL, SMALL_IMAGE_ITEMS_TABLET, SMALL_IMAGE_ITEMS_MOBILE));
            setSmallImageOffset(0);
        };
        updateItemsPerView();
        globalThis.window.addEventListener('resize', updateItemsPerView);
        return () => globalThis.window.removeEventListener('resize', updateItemsPerView);
    }, [getItemsPerView, normalizedGalleryType]);
    useEffect(() => {
        if (normalizedGalleryType !== LARGE_IMAGE) {
            return;
        }
        if (!globalThis.window)
            return;
        const updateItemsPerView = () => {
            setLargeImageItemsPerView(getItemsPerView(globalThis.window.innerWidth, LARGE_IMAGE_ITEMS_DESKTOP, LARGE_IMAGE_ITEMS_SMALL, LARGE_IMAGE_ITEMS_TABLET, LARGE_IMAGE_ITEMS_MOBILE));
            setLargeImageOffset(0);
        };
        updateItemsPerView();
        globalThis.window.addEventListener('resize', updateItemsPerView);
        return () => globalThis.window.removeEventListener('resize', updateItemsPerView);
    }, [getItemsPerView, normalizedGalleryType]);
    useEffect(() => {
        if (normalizedGalleryType !== SMALL_IMAGE) {
            return;
        }
        if (!globalThis.window)
            return;
        const getViewportWidth = () => {
            var _a, _b;
            const measuredWidth = (_b = (_a = smallImageCarouselViewportRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width) !== null && _b !== void 0 ? _b : 0;
            return measuredWidth > 0 ? measuredWidth : globalThis.window.innerWidth;
        };
        const updateViewportWidth = () => {
            setSmallImageViewportWidth(getViewportWidth());
        };
        updateViewportWidth();
        globalThis.window.addEventListener('resize', updateViewportWidth);
        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => {
                updateViewportWidth();
            })
            : null;
        if (resizeObserver && smallImageCarouselViewportRef.current) {
            resizeObserver.observe(smallImageCarouselViewportRef.current);
        }
        return () => {
            globalThis.window.removeEventListener('resize', updateViewportWidth);
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
        };
    }, [isDesktop, normalizedGalleryType]);
    useEffect(() => {
        if (normalizedGalleryType !== LARGE_IMAGE) {
            return;
        }
        if (!globalThis.window)
            return;
        const getViewportWidth = () => {
            var _a, _b;
            const measuredWidth = (_b = (_a = largeImageCarouselViewportRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width) !== null && _b !== void 0 ? _b : 0;
            return measuredWidth > 0 ? measuredWidth : globalThis.window.innerWidth;
        };
        const updateViewportWidth = () => {
            setLargeImageViewportWidth(getViewportWidth());
        };
        updateViewportWidth();
        globalThis.window.addEventListener('resize', updateViewportWidth);
        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => {
                updateViewportWidth();
            })
            : null;
        if (resizeObserver && largeImageCarouselViewportRef.current) {
            resizeObserver.observe(largeImageCarouselViewportRef.current);
        }
        return () => {
            globalThis.window.removeEventListener('resize', updateViewportWidth);
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
        };
    }, [isDesktop, normalizedGalleryType]);
    const smallImageMetrics = useMemo(() => getCarouselMetrics(categories.length, smallImageItemsPerView, SMALL_IMAGE_ITEM_WIDTH, smallImageViewportWidth), [categories.length, smallImageItemsPerView, smallImageViewportWidth]);
    const largeImageMetrics = useMemo(() => getCarouselMetrics(categories.length, largeImageItemsPerView, LARGE_IMAGE_ITEM_WIDTH, largeImageViewportWidth), [categories.length, largeImageItemsPerView, largeImageViewportWidth]);
    useEffect(() => {
        setSmallImageOffset((prev) => Math.min(prev, smallImageMetrics.maxOffset));
    }, [smallImageMetrics.maxOffset]);
    useEffect(() => {
        setLargeImageOffset((prev) => Math.min(prev, largeImageMetrics.maxOffset));
    }, [largeImageMetrics.maxOffset]);
    const scroll = (direction) => {
        if (!scrollContainerRef.current)
            return;
        const scrollAmount = 100;
        const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
        scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    };
    const renderArrowButton = (direction, onClick) => (_jsx("div", { className: clsx(classes.touchButtonWrapper, direction === 'left' ? classes.leftArrow : classes.rightArrow), children: _jsx("button", { className: classes.arrowButton, onClick: onClick, "aria-label": `Scroll categories ${direction}`, type: "button", children: _jsx(Icon, { icon: direction === 'left' ? 'chevron_left' : 'chevron_right' }) }) }));
    const renderTextIconVariant = () => (_jsx(CategoryLinksTextAndIcon, { categories: categories, wrapperClassName: cx(classes.textIconWrapper, isTextIconOverflowing && classes.textIconWrapperOverflow), itemClassName: classes.textIconItem, linkClassName: classes.categoryLink, sendCategoryCarouselClickAnalytics: sendCategoryCarouselClickAnalytics }));
    const renderSmallImageItems = () => categories.map((item, index) => (_jsx("div", { className: classes.smallImageCarouselItem, children: _jsx(CategoryLinksSmallImage, { category: Object.assign(Object.assign({}, item), { image: resolveGalleryImage(item) }), wrapperClassName: classes.smallImageWrapper, itemClassName: classes.smallImageItem, imageContainerClassName: classes.smallImageContainer, imageClassName: classes.smallImage, labelClassName: classes.smallImageLabel, labelContainerClassName: classes.smallImageLabelContainer, withWrapper: false, sendCategoryCarouselClickAnalytics: sendCategoryCarouselClickAnalytics }) }, `category-${item.label}-${index}`)));
    const renderSmallImageVariant = () => isDesktop ? (_jsx("div", { ref: smallImageCarouselViewportRef, className: classes.smallImageCarouselViewport, children: _jsxs("div", { className: classes.customCarouselList, style: { maxWidth: smallImageMetrics.viewportWidth }, children: [shouldShowSmallImageArrows &&
                    canScrollSmallImageLeft &&
                    renderArrowButton('left', () => scrollSmallImage('left')), _jsx("div", { "data-testid": "small-image-track", className: cx(classes.customCarouselTrack, smallImageMetrics.maxOffset === 0 && classes.customCarouselTrackCentered), style: { transform: `translate3d(-${smallImageOffset}px, 0, 0)` }, children: renderSmallImageItems() }), shouldShowSmallImageArrows &&
                    canScrollSmallImageRight &&
                    renderArrowButton('right', () => scrollSmallImage('right'))] }) })) : (_jsx("div", { "data-testid": "scroll-container", className: cx(classes.linksContainer, classes.smallImageLinksContainer, smallImageMetrics.maxOffset === 0 && classes.linksContainerCentered), children: renderSmallImageItems() }));
    const canScrollSmallImageLeft = smallImageOffset > 0;
    const canScrollSmallImageRight = smallImageOffset < smallImageMetrics.maxOffset - 0.5;
    const scrollSmallImage = (direction) => {
        if (direction === 'left') {
            setSmallImageOffset((prev) => Math.max(0, prev - smallImageMetrics.step));
            return;
        }
        setSmallImageOffset((prev) => Math.min(smallImageMetrics.maxOffset, prev + smallImageMetrics.step));
    };
    const renderLargeImageItems = () => categories.map((item, index) => (_jsx("div", { className: classes.largeImageCarouselItem, children: _jsx(CategoryLinksLargeImage, { category: Object.assign(Object.assign({}, item), { image: resolveGalleryImage(item) }), wrapperClassName: classes.largeImageWrapper, itemClassName: classes.largeImageItem, imageContainerClassName: classes.largeImageContainer, imageClassName: classes.largeImage, contentClassName: classes.largeImageContent, labelClassName: classes.largeImageLabel, labelContainerClassName: classes.largeImageLabelContainer, withWrapper: false, sendCategoryCarouselClickAnalytics: sendCategoryCarouselClickAnalytics }) }, `large-category-${item.label}-${index}`)));
    const renderLargeImageVariant = () => isDesktop ? (_jsx("div", { ref: largeImageCarouselViewportRef, className: classes.largeImageCarouselViewport, children: _jsxs("div", { className: classes.customCarouselList, style: { maxWidth: largeImageMetrics.viewportWidth }, children: [shouldShowLargeImageArrows &&
                    canScrollLargeImageLeft &&
                    renderArrowButton('left', () => scrollLargeImage('left')), _jsx("div", { "data-testid": "large-image-track", className: cx(classes.customCarouselTrack, largeImageMetrics.maxOffset === 0 && classes.customCarouselTrackCentered), style: { transform: `translate3d(-${largeImageOffset}px, 0, 0)` }, children: renderLargeImageItems() }), shouldShowLargeImageArrows &&
                    canScrollLargeImageRight &&
                    renderArrowButton('right', () => scrollLargeImage('right'))] }) })) : (_jsx("div", { "data-testid": "scroll-container", className: cx(classes.linksContainer, largeImageMetrics.maxOffset === 0 && classes.linksContainerCentered), children: renderLargeImageItems() }));
    const canScrollLargeImageLeft = largeImageOffset > 0;
    const canScrollLargeImageRight = largeImageOffset < largeImageMetrics.maxOffset - 0.5;
    const scrollLargeImage = (direction) => {
        if (direction === 'left') {
            setLargeImageOffset((prev) => Math.max(0, prev - largeImageMetrics.step));
            return;
        }
        setLargeImageOffset((prev) => Math.min(largeImageMetrics.maxOffset, prev + largeImageMetrics.step));
    };
    const renderVariant = () => {
        switch (normalizedGalleryType) {
            case TEXT_ICON:
                return renderTextIconVariant();
            case SMALL_IMAGE:
                return renderSmallImageVariant();
            case LARGE_IMAGE:
                return renderLargeImageVariant();
            default:
                return renderTextIconVariant();
        }
    };
    const shouldShowArrows = showArrows && isDesktop && normalizedGalleryType === TEXT_ICON;
    const shouldShowSmallImageArrows = showArrows && isDesktop && normalizedGalleryType === SMALL_IMAGE && smallImageMetrics.maxOffset > 0;
    const shouldShowLargeImageArrows = showArrows && isDesktop && normalizedGalleryType === LARGE_IMAGE && largeImageMetrics.maxOffset > 0;
    if (!categories.length) {
        return null;
    }
    return (_jsxs("div", { className: cx(classes.container, className), children: [title && (_jsx("h3", { className: cx(classes.categoryTitle, galleryType === SMALL_IMAGE && classes.smallImageVariantTitle), children: title })), _jsx("div", { className: classes.carouselWrapper, children: normalizedGalleryType === TEXT_ICON ? (_jsxs("div", { className: classes.textIconCarouselViewport, children: [shouldShowArrows && canScrollLeft && renderArrowButton('left', () => scroll('left')), _jsx("div", { "data-testid": "scroll-container", className: classes.linksContainer, ref: scrollContainerRef, children: renderVariant() }), shouldShowArrows && canScrollRight && renderArrowButton('right', () => scroll('right'))] })) : (renderVariant()) })] }));
};
export default CategoryLinkGallery;
